/**
 * Which OOO is worth showing.
 *
 * The calendar sync already drops blocks under 4h, but a week's total can still
 * be noise: one half-day out doesn't change a staffing decision. So OOO is only
 * displayed for a maker's week when it reaches half their weekly capacity (20h
 * at 40h/wk). Below that the week shows no OOO at all, as if none were stored.
 *
 * This is display-only. The stored employee_ooo rows are untouched, and the
 * Makers board's "Add OOO" mode still shows every value so it can be edited.
 */

/** OOO is shown once a week's total reaches this fraction of weekly capacity. */
export const OOO_DISPLAY_MIN_CAPACITY_FRACTION = 0.5;

/** Capacity assumed when a maker has none recorded, matching the OOO sync. */
const DEFAULT_WEEKLY_CAPACITY_HOURS = 40;

/** Hours a week's OOO must reach before it is displayed for this maker. */
export function oooDisplayThresholdHours(weeklyCapacityHours: number | null | undefined): number {
  const capacity =
    weeklyCapacityHours != null && weeklyCapacityHours > 0
      ? weeklyCapacityHours
      : DEFAULT_WEEKLY_CAPACITY_HOURS;
  return capacity * OOO_DISPLAY_MIN_CAPACITY_FRACTION;
}

/** The OOO hours to display for one maker's week: the stored value, or 0 below the threshold. */
export function displayedOooHours(
  hours: number,
  weeklyCapacityHours: number | null | undefined,
): number {
  return hours > 0 && hours >= oooDisplayThresholdHours(weeklyCapacityHours) ? hours : 0;
}

/**
 * One maker's week -> OOO map with sub-threshold weeks removed. Returns
 * undefined when nothing is left, matching how callers treat "no OOO".
 */
export function displayedOooByWeek(
  weeks: Map<number, number> | undefined,
  weeklyCapacityHours: number | null | undefined,
): Map<number, number> | undefined {
  if (!weeks) return undefined;
  const shown = new Map<number, number>();
  for (const [weekKey, hours] of weeks) {
    const displayed = displayedOooHours(hours, weeklyCapacityHours);
    if (displayed > 0) shown.set(weekKey, displayed);
  }
  return shown.size > 0 ? shown : undefined;
}

/**
 * The OOO map a maker's timeline row should render. A row that is itself in
 * "Add OOO" mode shows every stored value so small entries can be edited; any
 * other row, including a read-only one while the shared edit mode is on, gets
 * only the weeks that reach the display threshold.
 */
export function oooForTimelineRow(
  weeks: Map<number, number> | undefined,
  weeklyCapacityHours: number | null | undefined,
  rowIsOooEditable: boolean,
): Map<number, number> | undefined {
  return rowIsOooEditable ? weeks : displayedOooByWeek(weeks, weeklyCapacityHours);
}
